import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMenuStore } from '@/store/menuStore';
import { GuideButton, ROUTE_GUIDE_MAP } from '@/features/guide';

const MenuPathContainer = styled.div<{ width?: string }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[7]}px;
  width: ${({ width }) => width || '100%'};
  margin: 0;
  box-sizing: border-box;
`;

const CurrentMenu = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => {
    return theme.colors.text.primary;
  }};
  margin: 0;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]}px;
`;

const PathContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => {
    return theme.colors.text.secondary;
  }};
`;

const PathSeparator = styled.span`
  color: ${({ theme }) => {
    return theme.colors.border.container;
  }};
`;

// 메뉴 데이터 타입 정의
interface MenuItemType {
  id: string;
  label: string;
  icon?: string;
  children?: MenuItemType[];
  isActive?: boolean;
}

// Web용 메뉴 데이터 사용
import { webMenuData } from '@/menu/webMenu';

// 메뉴 경로 계산 함수
const getMenuPath = (itemId: string): { currentMenu: string; path: string[] } => {
  const findMenuItem = (items: MenuItemType[], targetId: string): MenuItemType | null => {
    for (const item of items) {
      if (item.id === targetId) {
        return item;
      }
      if (item.children) {
        const found = findMenuItem(item.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const findPath = (
    items: MenuItemType[],
    targetId: string,
    currentPath: string[] = []
  ): string[] | null => {
    for (const item of items) {
      const newPath = [...currentPath, item.label];
      if (item.id === targetId) {
        return newPath;
      }
      if (item.children) {
        const found = findPath(item.children, targetId, newPath);
        if (found) return found;
      }
    }
    return null;
  };

  const menuItem = findMenuItem(webMenuData, itemId);
  const path = findPath(webMenuData, itemId);

  return {
    currentMenu: menuItem?.label || '',
    path: path ? path.slice(0, -1) : [], // 현재 메뉴는 제외하고 경로만
  };
};

interface MenuPathProps {
  width?: string;
}

export const MenuPath: React.FC<MenuPathProps> = ({ width }) => {
  const { t } = useTranslation(['common', 'config']);
  const { activeItem } = useMenuStore();
  const { pathname } = useLocation();

  // 현재 라우트에 등록된 가이드가 있으면 제목 옆에 ? 버튼을 노출한다.
  const guideKey = ROUTE_GUIDE_MAP.find(entry => entry.match(pathname))?.guideKey;

  if (!activeItem) {
    return null;
  }

  const { currentMenu, path } = getMenuPath(activeItem);

  return (
    <MenuPathContainer width={width}>
      <TitleGroup>
        <CurrentMenu data-tour={guideKey ? `${guideKey}-intro` : undefined}>
          {t(currentMenu)}
        </CurrentMenu>
        {guideKey && <GuideButton guideKey={guideKey} iconOnly iconSize={22} />}
      </TitleGroup>
      {path.length > 0 && (
        <PathContainer>
          {path.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <PathSeparator>/</PathSeparator>}
              <span>{t(item)}</span>
            </React.Fragment>
          ))}
        </PathContainer>
      )}
    </MenuPathContainer>
  );
};
